import { Session } from "ezStore";
import { Cargo, CargoActionForm, CargoForm, CargoLinkedCharter } from "types/index";
import { hasPluginPermission } from "utils";
import { numberOrNull } from "utils/numberUtils";
import { previewSimulatedTimestamps } from "view/cargos/utils/CargoActionUtils";
import moment from "moment";
import { CallType, CharterSubtype, CharterType } from "__generated__/enums";

export enum CargoTabs {
	Actions = "actions",
	Oog = "oog",
	Deepsea = "deepsea",
	VoyageSchedule = "voyageSchedule",
	Relations = "relations"
}

export const defaultCargoAction = (type: CallType): CargoActionForm => ({
	id: null,
	type: type,
	location: null,
	weight: null,
	volume: null,
	date: null,
	windowFrom: null,
	windowTo: null,
	displayAsWindow: false,
	surveyor: null,
	notes: "",
	terminalPlanningNotes: "",
	connectingVisitNotes: "",
	includedForLaytimeCalculation: true,
	simulatedTimestamp: true,
});

export const getCargoFormDefaultValues = (session: Session, charter?: CargoLinkedCharter) => {
	return {
		id: 0,
		containerNumber: "",
		masterBundleContainerNumber: "",
		cargoNumber: "",
		nominationNumber: "",
		product: undefined,
		productDetails: "",
		loadingReference: "",
		dischargingReference: "",
		containerType: undefined,
		weight: undefined,
		tareWeight: undefined,
		grossWeight: undefined,
		verifiedGrossWeight: undefined,
		declaredFinalWeight: undefined,
		weightToleranceType: undefined,
		weightOptionType: undefined,
		weightOptionPercentage: undefined,
		empty: false,
		desiredTemperature: undefined,
		temperatureRangeLower: undefined,
		temperatureRangeUpper: undefined,
		sealed: false,
		sealNumber: "",
		waNumber: "",
		bookingReference: "",
		loaded: undefined,
		discharged: undefined,
		transportType: undefined,
		unNumbers: undefined,
		isInhibitedProduct: false,
		isNitrogenBlanketingRequired: false,
		isNitrogenPurgingRequired: false,
		waterLevelOptimised: false,
		isTemperatureControlRequired: false,
		isCleaningRequired: false,
		reeferCheckType: undefined,
		imoClass: "",
		billOfLadingNumber: "",
		humidity: undefined,
		remarks: "",
		scacCode: "",
		scacCodeDischarging: "",
		damage: "",
		releasedForBarge: false,
		releasedForTrain: false,
		releasedForTruck: false,
		deliveryCondition: undefined,
		revenue: undefined,

		supplier: undefined,
		consignee: undefined,
		containerOperator: undefined,

		overheight: undefined,
		overlength: undefined,
		overlengthBehind: undefined,
		overwidthRight: undefined,
		overwidthLeft: undefined,
		doorOpen: false,
		chains: false,
		generator: false,

		deepseaShipNameLoading: "",
		deepseaShipImoLoading: undefined,
		deepseaVoyageNumberLoading: "",
		deepseaEtaLoading: undefined,
		deepseaShipNameDischarging: "",
		deepseaShipImoDischarging: undefined,
		deepseaVoyageNumberDischarging: "",
		deepseaEtaDischarging: undefined,
		deepseaEstimateDischarge: undefined,

		previousInlandTerminal: undefined,
		nextInlandTerminal: undefined,
		previousPort: "",
		nextPort: "",
		finalPort: "",

		actions: [ defaultCargoAction(CallType.Loading), defaultCargoAction(CallType.Discharging) ],

		charter: charter ? {
			id: charter.id,
			type: CharterType.Sales,
			subtype: CharterSubtype.Spot,
		} : undefined,

		consignor: charter?.debtor ? {
			id: charter.debtor.id,
			name: charter.debtor.name,
		} : undefined,

		nominatedWeight: undefined,
		nominatedVolume: undefined,

		isWeightPrimary: hasPluginPermission(session, "cargoUsePrimaryWeight"),
		isVolumePrimary: hasPluginPermission(session, "cargoUsePrimaryVolume"),
	};
};

const getCargoActionsInput = (actions: CargoActionForm[], useSimulatedTimestamps: boolean) => {
	if (!useSimulatedTimestamps) {
		return actions.map(x => ({
			...x,
			date: x.date != null ? moment(x.date).format("YYYY-MM-DD") : null,
		}));
	}

	const preview = previewSimulatedTimestamps(actions);

	return actions.map((x, i) => {
		const actionPreview = preview[i];

		const actualAction = x.simulatedTimestamp ? actionPreview : x;

		return {
			...actualAction,
			date: actualAction.date != null ? moment(actualAction.date).format("YYYY-MM-DD") : null,
		};
	});
};

export const getCargoFormMutationInput = (data: CargoForm, useSimulatedTimestamps: boolean) => {
	const cargoId = data.id && Number(data.id) ? Number(data.id) : 0;

	// @ts-ignore this field is queried in the request, but irrelevant when creating / editing a cargo
	delete data.totalLoadingWeight;
	// @ts-ignore this field is queried in the request, but irrelevant when creating / editing a cargo
	delete data.volume;

	const actions = getCargoActionsInput(data.actions ?? [], useSimulatedTimestamps) ?? [];

	return {
		...data,
		id: cargoId,
		weight: numberOrNull(data.weight),
		grossWeight: numberOrNull(data.grossWeight),
		tareWeight: numberOrNull(data.tareWeight),
		unNumbers: data.unNumbers?.map(unNumber => unNumber.unNumber) ?? [],
		containerType: data.containerType?.isoTypeCode,
		nextInlandTerminal: data.nextInlandTerminal ? {
			id: data.nextInlandTerminal.id,
		} : undefined,
		previousInlandTerminal: data.previousInlandTerminal ? {
			id: data.previousInlandTerminal.id,
		} : undefined,
		deliveryCondition: data.deliveryCondition ? {
			id: data.deliveryCondition.id,
		} : undefined,
		product: data.product && {
			id: data.product.id,
		},
		charter: {
			id: data.charter?.id,
			type: data.charter?.type,
		},
		containerOperator: data.containerOperator ? {
			id: data.containerOperator.id,
		} : undefined,
		voyage: data.voyage ? {
			id: data.voyage.id,
		} : undefined,
		supplier: data.supplier ? {
			id: data.supplier.id,
			name: data.supplier.name,
		} : undefined,
		consignee: data.consignee ? {
			id: data.consignee.id,
			name: data.consignee.name,
		} : undefined,
		consignor: data.consignor ? {
			id: data.consignor.id,
			name: data.consignor.name,
		} : undefined,
		declaredFinalWeight: numberOrNull(data.declaredFinalWeight),
		desiredTemperature: numberOrNull(data.desiredTemperature),
		weightOptionPercentage: numberOrNull(data.weightOptionPercentage),
		actions: actions.map(action => ({
			id: action.id || null,
			cargo: cargoId,
			location: action.location,
			type: action.type,
			date: action.displayAsWindow ? null : action.date,
			weight: numberOrNull(action.weight),
			volume: numberOrNull(action.volume),
			windowFrom: action.displayAsWindow ? action.windowFrom : null,
			windowTo: action.displayAsWindow ? action.windowTo : null,
			displayAsWindow: action.displayAsWindow,
			surveyor: action.surveyor ? {
				id: action.surveyor.id,
			} : undefined,
			reference: action.reference,
			notes: action.notes,
			limitType: action.limitType,
			limitValue: numberOrNull(action.limitValue),
			terminalPlanningNotes: action.terminalPlanningNotes,
			connectingVisitNotes: action.connectingVisitNotes,
			includedForLaytimeCalculation: action.includedForLaytimeCalculation,
			shipToShip: action.shipToShip,
			connectingShipPortVisit: action.connectingShipPortVisit?.ship ?
				action.connectingShipPortVisit.existingConnectingShipPortVisit ? {
					id: action.connectingShipPortVisit.existingConnectingShipPortVisit.id,
					ship: {
						id: action.connectingShipPortVisit.existingConnectingShipPortVisit.ship.id,
						name: action.connectingShipPortVisit.existingConnectingShipPortVisit.ship.name,
					},
					location: action.connectingShipPortVisit.location ?? action.connectingShipPortVisit.existingConnectingShipPortVisit.location,
					agent: action.connectingShipPortVisit.agent ? {
						id: action.connectingShipPortVisit.agent.id,
					} : null,
					from: action.connectingShipPortVisit.from ?? undefined,
					until: action.connectingShipPortVisit.until ?? undefined,
					planningNotes: action.connectingShipPortVisit.planningNotes,
				} : {
					id: action.connectingShipPortVisit.id,
					ship: {
						id: action.connectingShipPortVisit.ship.id,
						name: action.connectingShipPortVisit.ship.name,
					},
					location: action.connectingShipPortVisit.location,
					agent: action.connectingShipPortVisit.agent ? {
						id: action.connectingShipPortVisit.agent.id,
					} : null,
					from: action.connectingShipPortVisit.from ?? undefined,
					until: action.connectingShipPortVisit.until ?? undefined,
					planningNotes: action.connectingShipPortVisit.planningNotes,
				} : null,
			connectingShipBerthing: action.connectingShipBerthing?.location?.id ? {
				id: action.connectingShipBerthing.id,
				visit: { id: action.connectingShipBerthing.visit?.id },
				window: { from: action.connectingShipBerthing.window?.from, to: action.connectingShipBerthing.window?.to },
				comment: action.connectingShipBerthing.comment,
				location: { id: action.connectingShipBerthing.location.id },
			} : null,
			simulatedTimestamp: action.simulatedTimestamp,
		})),
		firstLoadingAction: data.firstLoadingAction ? {
			...{ ...data.firstLoadingAction, start: undefined, end: undefined },
			cargo: cargoId,
			surveyor: data.firstLoadingAction.surveyor ? {
				id: data.firstLoadingAction.surveyor.id,
			} : undefined,
			connectingShipBerthing: data.firstLoadingAction.connectingShipBerthing?.location?.id ? {
				visit: { id: data.firstLoadingAction.connectingShipBerthing.visit?.id },
				window: { from: data.firstLoadingAction.connectingShipBerthing.window?.from, to: data.firstLoadingAction.connectingShipBerthing.window?.to },
				comment: data.firstLoadingAction.connectingShipBerthing.comment,
				location: { id: data.firstLoadingAction.connectingShipBerthing.location.id },
			} : null,
		} : undefined,
		lastDischargingAction: data.lastDischargingAction ? {
			...{ ...data.lastDischargingAction, start: undefined, end: undefined },
			surveyor: data.lastDischargingAction.surveyor ? {
				id: data.lastDischargingAction.surveyor.id,
			} : undefined,
			cargo: cargoId,
			connectingShipBerthing: data.lastDischargingAction.connectingShipBerthing?.location?.id ? {
				visit: { id: data.lastDischargingAction.connectingShipBerthing.visit?.id },
				window: { from: data.lastDischargingAction.connectingShipBerthing.window?.from, to: data.lastDischargingAction.connectingShipBerthing.window?.to },
				comment: data.lastDischargingAction.connectingShipBerthing.comment,
				location: { id: data.lastDischargingAction.connectingShipBerthing.location.id },
			} : null,
		} : undefined,
		nominatedWeight: numberOrNull(data.nominatedWeight),
		nominatedVolume: numberOrNull(data.nominatedVolume),
	};
};

export const convertWeightAndVolume = (quantity: "weight" | "volume", densityKgM3: number, value?: number) => {
	const fieldValue = value || 0;

	const weight = quantity === "weight" ? fieldValue : Number(Number(fieldValue * densityKgM3).toFixed(3));
	const volume = quantity === "volume" ? fieldValue : Number(Number(fieldValue / densityKgM3).toFixed(3));

	return { weight, volume };
};

export const distributeWeightAndVolumeOverActions = (actions: CargoActionForm[], nominatedWeight?: number, nominatedVolume?: number) => {
	const updatedActions = [ ...actions ];

	const loadingActionIndices: number[] = [];
	const dischargingActionSets: number[][] = [];
	let currentSet: number[] = [];

	// Create sets of loading - discharging actions
	actions.forEach((action, index) => {
		if (action.type === CallType.Loading) {
			if (currentSet.length) {
				dischargingActionSets.push(currentSet);
				currentSet = [];
			}
			loadingActionIndices.push(index);
		} else if (action.type === CallType.Discharging) {
			currentSet.push(index);
		}
	});

	if (currentSet.length) {
		dischargingActionSets.push(currentSet);
	}

	const loadingWeight = nominatedWeight != undefined ? Number((nominatedWeight / loadingActionIndices.length).toFixed(3)) : undefined;
	const loadingVolume = nominatedVolume != undefined ? Number((nominatedVolume / loadingActionIndices.length).toFixed(3)) : undefined;

	// Distribute weight to loading actions first
	loadingActionIndices.forEach((actionIndex, index) => {
		updatedActions[actionIndex] = {
			...updatedActions[actionIndex],
			weight: loadingWeight ?? null,
			volume: loadingVolume ?? null,
		};

		// Then, distribute weight to discharging actions
		const dischargingActionSet = dischargingActionSets[index];

		const dischargingWeight = loadingWeight ? Number((loadingWeight / dischargingActionSet.length).toFixed(3)) : undefined;
		const dischargingVolume = loadingVolume ? Number((loadingVolume / dischargingActionSet.length).toFixed(3)) : undefined;

		dischargingActionSet.forEach(actionIndex => {
			updatedActions[actionIndex] = {
				...updatedActions[actionIndex],
				weight: dischargingWeight ?? null,
				volume: dischargingVolume ?? null,
			};
		});
	});

	return updatedActions;
};

export const calculateMaxWeightAndMaxVolume = (sortedCargoActions: { type: CallType; weight?: number | null; volume?: number | null; }[]) => {
	const _total = [ 0, 0 ];
	return sortedCargoActions
		.reduce((maxTotal, action) => {
			// Find the max weight and volume planned across all the actions
			switch (action.type) {
				case CallType.Loading:
					_total[0] += action.weight ?? 0;
					_total[1] += action.volume ?? 0;
					break;
				case CallType.Discharging:
					_total[0] -= action.weight ?? 0;
					_total[1] -= action.volume ?? 0;
					break;
			}
			// Check if this is the maximum weight
			if (_total[0] > maxTotal[0]) {
				maxTotal[0] = _total[0];
			}
			// Check if this is the maximum volume
			if (_total[1] > maxTotal[1]) {
				maxTotal[1] = _total[1];
			}
			return maxTotal;
		}, [ 0, 0 ])
		.map(it => Math.ceil(it));
};

export const cargoHasServices = (cargo: Pick<Cargo, "isHoseRentalRequired" | "isInhibitedProduct" | "isNitrogenBlanketingRequired" | "isNitrogenPurgingRequired" | "isTemperatureControlRequired" | "isCleaningRequired">) => {
	return cargo.isHoseRentalRequired ||
	cargo.isInhibitedProduct ||
	cargo.isNitrogenBlanketingRequired ||
	cargo.isNitrogenPurgingRequired ||
	cargo.isTemperatureControlRequired ||
	cargo.isCleaningRequired;
};
